import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { getPass } from "../lib/api";
import { useLang } from "../context/LangContext";
import { TREATMENTS } from "../lib/content";
import { ShieldCheck, LogIn, LogOut, Clock, XCircle } from "lucide-react";

const LOCALES = { fr: "fr-FR", en: "en-GB", it: "it-IT", ru: "ru-RU" };

const STATUS_UI = {
  new: {
    fr: "En attente de confirmation", en: "Pending confirmation", it: "In attesa di conferma", ru: "Ожидает подтверждения",
    icon: Clock, tone: "muted",
  },
  confirmed: {
    fr: "Confirmé — accès autorisé", en: "Confirmed — access granted", it: "Confermato — accesso autorizzato", ru: "Подтверждено — доступ разрешён",
    icon: ShieldCheck, tone: "gold",
  },
  "checked-in": {
    fr: "Entré", en: "Checked in", it: "Entrato", ru: "Вход выполнен",
    icon: LogIn, tone: "gold",
  },
  "checked-out": {
    fr: "Sorti", en: "Checked out", it: "Uscito", ru: "Выход выполнен",
    icon: LogOut, tone: "muted",
  },
  cancelled: {
    fr: "Annulé", en: "Cancelled", it: "Annullato", ru: "Отменено",
    icon: XCircle, tone: "red",
  },
};

const TITLES = {
  fr: { eyebrow: "PASS D'ACCÈS · RIVIERA SHOCKWAVES", not_found: "Pass introuvable.", invalid: "Ce code ne correspond à aucune réservation.", home: "Retour au site" },
  en: { eyebrow: "ACCESS PASS · RIVIERA SHOCKWAVES", not_found: "Pass not found.", invalid: "This code matches no booking.", home: "Back to the site" },
  it: { eyebrow: "PASS DI ACCESSO · RIVIERA SHOCKWAVES", not_found: "Pass non trovato.", invalid: "Questo codice non corrisponde a nessuna prenotazione.", home: "Torna al sito" },
  ru: { eyebrow: "ПРОПУСК · RIVIERA SHOCKWAVES", not_found: "Пропуск не найден.", invalid: "Этот код не соответствует ни одной записи.", home: "Вернуться на сайт" },
};

export default function PassCheck() {
  const { code } = useParams();
  const { lang } = useLang();
  const t = TITLES[lang] || TITLES.en;
  const locale = LOCALES[lang] || "en-GB";
  const [pass, setPass] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getPass(code).then(setPass).catch(() => setError(true));
  }, [code]);

  const st = pass ? (STATUS_UI[pass.status] || STATUS_UI.new) : null;
  const svc = pass ? TREATMENTS.find((x) => x.key === pass.service) : null;
  const dateFmt = pass
    ? new Date(`${pass.date}T00:00:00`).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <main className="pt-32 pb-24 min-h-[90vh]" data-testid="page-pass">
      <div className="container-luxe max-w-lg">
        <div className="text-center overline">{t.eyebrow}</div>

        {error && (
          <div className="mt-10 hairline bg-[var(--surface-1)] p-10 text-center" data-testid="pass-not-found">
            <XCircle size={28} className="mx-auto text-red-400" />
            <h1 className="font-display text-2xl text-[var(--ink)] mt-4 font-light">{t.not_found}</h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">{t.invalid}</p>
          </div>
        )}

        {!pass && !error && (
          <div className="mt-10 hairline bg-[var(--surface-1)] p-10 text-center text-[var(--ink)]/40 text-sm animate-pulse">…</div>
        )}

        {pass && st && (
          <div className="mt-10 hairline bg-[var(--surface-1)] overflow-hidden" data-testid="pass-card">
            <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, var(--gold), var(--gold-soft))" }} />
            <div className="p-8 md:p-10 text-center">
              <div
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-display font-semibold ${
                  st.tone === "gold"
                    ? "border-[var(--gold)]/50 text-[var(--gold)] bg-[var(--gold-soft)]/10"
                    : st.tone === "red"
                    ? "border-red-300 text-red-400 bg-red-50"
                    : "border-[var(--ink)]/20 text-[var(--ink)]/60"
                }`}
                data-testid="pass-status"
              >
                <st.icon size={14} /> {st[lang] || st.en}
              </div>

              <h1 className="font-serif-luxe text-3xl text-[var(--ink)] mt-6" data-testid="pass-name">{pass.name}</h1>

              <div className="mt-6 space-y-2 text-sm text-[var(--ink)]/80 font-body">
                <div data-testid="pass-service">{svc ? (svc[lang] || svc.fr) : pass.service}</div>
                <div className="capitalize" data-testid="pass-datetime">{dateFmt} · {pass.time}</div>
                {pass.location && <div data-testid="pass-location">{pass.location}</div>}
              </div>

              <div className="mt-8 flex justify-center">
                <div className="p-3 bg-white hairline rounded-md">
                  <QRCodeSVG value={pass.pass_url || `${window.location.origin}/pass/${pass.pass_code}`} size={168} level="M" fgColor="#0E0E12" data-testid="pass-qr" />
                </div>
              </div>
              <div className="mt-3 font-mono text-xs text-[var(--ink)]/50 tracking-widest" data-testid="pass-code">{pass.pass_code}</div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/" className="text-[11px] uppercase tracking-[0.2em] font-display text-[var(--ink)]/50 hover:text-[var(--gold)] transition-colors" data-testid="pass-home">
            {t.home}
          </Link>
        </div>
      </div>
    </main>
  );
}
